import { generateUUID } from '@/domain/shared/generateUUID';
import { AField } from '@/domain/field/AField';
import { graduationBehaviorFallback } from '@/domain/image/graduationBehaviorFallback';
import { imageQualityChecker } from '@/domain/image/imageQualityChecker';
import { isVectorFilename } from '@/utils/isVectorFilename';
import { CANVAS_DPI } from '@/domain/product/productDimensions';
import type { IImageBehavior } from '@/domain/image/IImageBehavior';
import type { TImagePropertyPatch } from '@/domain/image/TImagePropertyPatch';
import type { TPosition2D } from '@/domain/shared/TPosition2D';
import type { TScale2D } from '@/domain/shared/TScale2D';
import type { TSide } from '@/domain/shared/TSide';
import type { TSize2D } from '@/domain/shared/TSize2D';
import type { TImageFieldJSONData } from '@/domain/image/imageFieldSchema';

type TUploadKind = 'cutPath' | 'helpGrid' | 'user';

interface IImageField {
    behavior: IImageBehavior | undefined;
    filename: string | undefined;
    frameSize: TSize2D | undefined;
    isLocked: boolean;
    opacity: number;
    position: TPosition2D;
    rotationDeg: number;
    scale: TScale2D;
    side: TSide;
    size: TSize2D | undefined;
    uploadType: TUploadKind;
    url: string | undefined;
    uuid: string | undefined;
}

const DEFAULT_IMAGE_FIELD: IImageField = {
    behavior: undefined,
    filename: undefined,
    frameSize: undefined,
    isLocked: false,
    opacity: 1,
    position: { x: 0, y: 0 },
    rotationDeg: 0,
    scale: { x: 1, y: 1 },
    side: 'recto',
    size: undefined,
    uploadType: 'user',
    url: undefined,
    uuid: undefined,
};

type TImageFieldParameters = Partial<IImageField> & { id?: string };

class ImageField extends AField implements IImageField {
    readonly kind = 'image' as const;
    behavior: IImageBehavior | undefined;
    filename: string | undefined;
    opacity: number;
    size: TSize2D | undefined;
    uploadType: TUploadKind;
    url: string | undefined;
    uuid: string | undefined;

    constructor(params: TImageFieldParameters = {}) {
        const merged = { ...DEFAULT_IMAGE_FIELD, ...params };
        super({
            frameSize: merged.frameSize,
            id: merged.id,
            isLocked: merged.isLocked,
            position: merged.position,
            rotationDeg: merged.rotationDeg,
            scale: merged.scale,
            side: merged.side,
        });
        this.behavior = merged.behavior ? { ...merged.behavior } : undefined;
        this.filename = merged.filename;
        this.opacity = merged.opacity;
        this.size = merged.size ? { ...merged.size } : undefined;
        this.uploadType = merged.uploadType;
        this.url = merged.url;
        this.uuid = merged.uuid;
    }

    // Nothing to draw without a url — the bitmap *is* the content.
    hasContent(): boolean {
        return this.url !== undefined;
    }

    // Derived from the current size + scale — no need to persist or
    // notify separately. Anyone reading it after a transform sees the
    // fresh value.
    get hasQualityWarning(): boolean {
        if (!this.size) {
            return false;
        }
        return imageQualityChecker({
            canvasDpi: CANVAS_DPI,
            isVector: isVectorFilename(this.filename),
            scaleX: this.scale.x,
            scaleY: this.scale.y,
        });
    }

    applyProperty(patch: TImagePropertyPatch): void {
        if (patch.opacity !== undefined) {
            this.opacity = patch.opacity;
        }
        if (patch.sourceUrl !== undefined) {
            this.url = patch.sourceUrl;
        }
    }

    clone(): ImageField {
        return ImageField.fromJSON({ ...this.toJSON(), id: generateUUID() });
    }

    toJSON(): TImageFieldJSONData {
        return {
            behavior: this.behavior,
            filename: this.filename,
            // Yup schema uses null for absent frameSize; entity uses undefined.
            frameSize: this.frameSize ?? null,
            hasQualityWarning: this.hasQualityWarning,
            id: this.id,
            isLocked: this.isLocked,
            kind: 'image',
            opacity: this.opacity,
            position: this.position,
            rotationDeg: this.rotationDeg,
            scale: this.scale,
            side: this.side,
            // Yup schema uses null for absent size; entity uses undefined.
            size: this.size ?? null,
            uploadType: this.uploadType,
            url: this.url,
            uuid: this.uuid,
        };
    }

    // Legacy JSON payloads still ship `hasQualityWarning` — ignored on
    // load, the getter always recomputes from size + scale.
    static fromJSON(json: TImageFieldJSONData): ImageField {
        return new ImageField({
            id: json.id,
            behavior: json.behavior ?? graduationBehaviorFallback(json.filename),
            filename: json.filename,
            frameSize: json.frameSize ?? undefined,
            isLocked: json.isLocked,
            opacity: json.opacity,
            position: json.position,
            rotationDeg: json.rotationDeg,
            scale: json.scale,
            side: json.side,
            size: json.size ?? undefined,
            uploadType: json.uploadType as TUploadKind,
            url: json.url,
            uuid: json.uuid,
        });
    }
}

export { ImageField };
export type { TImageFieldParameters };
